import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import {BaseDatasource} from "@shared/base/base-datasource";
import { MatSort, MatSortHeader } from "@angular/material/sort";
import {MatPaginator} from "@angular/material/paginator";
import {merge, Subscription} from "rxjs";
import {debounceTime, tap} from "rxjs/operators";
import { FormControl, FormGroup, UntypedFormGroup, ReactiveFormsModule } from "@angular/forms";
import {QuotationRelationApi} from "@app/quotation/shared/quotation-relation.api";
import {QuotationRelation} from "@app/quotation/shared/quotation-relation";
import {BaseList, pageSizeDefault, pageSizeOptions, Pagination, SearchFieldGroup, SearchOperator} from "@shared/base";
import { MatToolbar } from '@angular/material/toolbar';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
    templateUrl: './offer-relation-select-dialog.component.html',
    styleUrls: ['./offer-relation-select-dialog.component.scss'],
    imports: [MatToolbar, CdkScrollable, MatDialogContent, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatIconButton, MatSuffix, MatIcon, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatTooltip, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, MatDialogActions, MatPaginator, MatButton]
})
export class OfferRelationSelectDialogComponent extends BaseList<QuotationRelation> implements OnInit, OnDestroy, AfterViewInit {
  dialogRef = inject<MatDialogRef<OfferRelationSelectDialogComponent>>(MatDialogRef);
  protected listApi = inject(QuotationRelationApi);
  protected listCachePrefix = 'offerRelation';

  title: string;
  dataSource: BaseDatasource<QuotationRelation>;
  displayedColumns = ['firstName', 'familyName', 'companyName', 'address', 'city', 'source'];
  filterForm: FormGroup;
  filteredOptions!: Subscription;
  searchField: boolean;

  @ViewChild(MatSort, {static: true}) sort: MatSort;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
  @ViewChild('searchInput') searchInput!: ElementRef;

  constructor() {
    super();
    const data = inject(MAT_DIALOG_DATA);

    this.filterForm = new FormGroup({
      name: new FormControl<string | undefined>(undefined)
    });

    this.title = data.title;
  }

  ngOnInit() {
    super.ngOnInit();

    this.filteredOptions = this.filterForm.valueChanges.pipe(
      debounceTime(500)
    ).subscribe(() => {
      this.loadData();
    });
  }

  ngAfterViewInit() {
    merge(this.sort.sortChange, this.paginator.page).pipe(
      tap(() => {
        this.loadData();
      })
    ).subscribe();

    this.loadData();

    this.showSearch();
  }

  ngOnDestroy() {
    this.filteredOptions.unsubscribe();
    super.ngOnDestroy();
  }

  defaultListForm(): UntypedFormGroup {
    return this.formBuilder.group({manufacturer: null});
  }

  defaultPagination(): Pagination {
    return new Pagination(0, pageSizeDefault, 'familyName', 'desc');
  }

  prepareSearch(values: Record<string, string>): SearchFieldGroup {
    const group: SearchFieldGroup = {
      type: 'AND',
      fields: [],
      groups: []
    };

    const properties = Object.keys(values);
    properties.forEach(value => {
      group.fields.push({
        fieldName: value,
        operator: SearchOperator.CONTAINS,
        value: values[value]
      });
    });

    return group;
  }

  showSearch() {
    this.searchField = !this.searchField;

    //Otherwise the element isn't visible and the focus will not work
    setTimeout(()=>{
      this.searchInput.nativeElement.focus();
    }, 250);
  }

  select(offerRelation: QuotationRelation) {
    this.dialogRef.close(offerRelation);
  }

  close() {
    this.dialogRef.close(false);
  }

  protected readonly pageSizeOptions = pageSizeOptions;
}
